import Route from '@ember/routing/route';
import { type Registry as Services, service } from '@ember/service';
import { task } from 'ember-concurrency';
import {
  formatUrl,
  type FormatUrlParams,
} from 'frontend-utils/utils/url/format-url';
import type { BeniMarketComposition } from 'lumen/models';
import type AjaxService from 'lumen/services/ajax';
import type { Transition } from 'lumen/services/transition';

export default class HotelBenchmarkInsightMarketCompositionRoute extends Route {
  queryParams = {
    activeTab: {
      refreshModel: false,
    },
  };

  @service declare ajax: AjaxService;
  @service declare alerting: Services['alerting'];
  @service declare errorTracking: Services['error-tracking'];
  @service('hotel-and-market')
  declare hotelAndMarketService: Services['hotel-and-market'];

  @service declare store: Services['store'];
  @service declare transition: Services['transition'];

  marketCompositionTask = task({ restartable: true }, async () => {
    try {
      const params = {
        typeKey: 'benchmarkinsight/market_composition',
        subscription_id: this.hotelAndMarketService.hotel?.id ?? null,
      };

      return await this.store.queryRecord<BeniMarketComposition>(
        'benchmark-insight/market-composition',
        params,
      );
    } catch (error) {
      this.errorTracking.captureException(error);
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      this.alerting.showErrorAlert();
    }

    return null;
  });

  beforeModel(transition: Transition): void {
    this.transition.validateBenchmarkInsightMarketCompositionQueryParams(
      transition,
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  async fetchMarketComposition() {
    try {
      const subscriptionId = this.hotelAndMarketService.hotel?.id;
      if (subscriptionId) {
        const queryParams: FormatUrlParams = {
          subscription_id: subscriptionId,
        };
        const marketCompositionUrl = formatUrl(
          '/api/v3/benchmarkinsight/market_composition/',
          queryParams,
          this.ajax.host,
        );
        return await this.ajax.request(marketCompositionUrl);
      }
      return null;
    } catch (error) {
      this.errorTracking.captureException(error);
      return null;
    }
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  model() {
    const model = {
      marketComposition: this.marketCompositionTask,
    };

    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    model.marketComposition.perform();

    return model;
  }
}
